import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import DateInputBR from "@/components/DateInputBR";
import { fmtDate } from "@/lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Users2, Plus, Search, AlertTriangle, RefreshCw, FileDown,
  MoreVertical, Pencil, Trash2, History as HistoryIcon, UserPlus, Settings2,
  Edit2,
  Eye, LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";

const SHIFT_LABELS = {
  ADMIN: "Horário Administrativo",
  A: "Turno A",
  B: "Turno B",
  C: "Turno C",
  D: "Turno D",
};

export default function WorkBoardDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin", "rh_operacional");
  const canAdmin = hasRole("rh_admin", "master");

  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [compact, setCompact] = useState(false);
  const [flash, setFlash] = useState(null);

  // Modals
  const [subOpen, setSubOpen] = useState(false);
  const [subForm, setSubForm] = useState({ name: "", display_order: 99 });
  const [subEditing, setSubEditing] = useState(null);

  const [includeOpen, setIncludeOpen] = useState(false);
  const [includeForm, setIncludeForm] = useState({ employee_id: "", work_subsector_id: "", work_shift_type: "A", work_notes: "" });
  const [empSearchQ, setEmpSearchQ] = useState("");
  const [empResults, setEmpResults] = useState([]);

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveCtx, setMoveCtx] = useState(null); // {employee, currentSub, currentShift}

  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeCtx, setRemoveCtx] = useState(null);
  const [clearFields, setClearFields] = useState(false);

  const [inconsOpen, setInconsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/work-boards/${id}/grid`);
      setGrid(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  // Flash effect on recent updates
  const triggerFlash = (empId) => {
    setFlash(empId);
    setTimeout(() => setFlash(null), 1600);
  };

  // Filter employees by search
  const filterCells = useMemo(() => {
    if (!grid) return {};
    if (!search.trim()) return grid.cells;
    const q = search.toLowerCase();
    const out = {};
    for (const [subId, cols] of Object.entries(grid.cells)) {
      out[subId] = {};
      for (const [shift, emps] of Object.entries(cols)) {
        const filtered = emps.filter(e =>
          (e.nome || "").toLowerCase().includes(q) ||
          (e.matricula || "").toLowerCase().includes(q) ||
          (e.cargo || "").toLowerCase().includes(q)
        );
        if (filtered.length) out[subId][shift] = filtered;
      }
    }
    return out;
  }, [grid, search]);

  // Employee search for include modal
  useEffect(() => {
    if (!includeOpen) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/work-boards/_employees/search?q=${encodeURIComponent(empSearchQ)}`);
        setEmpResults(data);
      } catch (e) {
        console.error("Employee search failed:", e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [empSearchQ, includeOpen]);

  // ------- Subsector actions -------
  const openNewSub = () => { setSubEditing(null); setSubForm({ name: "", display_order: (grid?.subsectors?.length || 0) + 1 }); setSubOpen(true); };
  const openEditSub = (s) => { setSubEditing(s); setSubForm({ name: s.name, display_order: s.display_order }); setSubOpen(true); };
  const saveSub = async () => {
    try {
      if (subEditing) {
        await api.put(`/work-subsectors/${subEditing.id}`, { ...subForm, work_board_id: id, is_active: true });
      } else {
        await api.post(`/work-boards/${id}/subsectors`, { ...subForm, work_board_id: id, is_active: true });
      }
      toast.success("Sub-setor salvo");
      setSubOpen(false); loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeSub = async (s) => {
    if (!window.confirm(`Remover o sub-setor "${s.name}"?`)) return;
    try {
      await api.delete(`/work-subsectors/${s.id}`);
      toast.success("Sub-setor removido");
      loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ------- Include employee OR create empty position -------
  const openInclude = (subId = "", shift = "A") => {
    setIncludeForm({
      mode: "employee",              // "employee" | "position"
      employee_id: "",
      work_subsector_id: subId || grid?.subsectors?.[0]?.id || "",
      work_shift_type: shift,
      work_notes: "",
      cargo: "",
      status: "vaga_aberta",
      data_prevista_necessidade: "",
    });
    setEmpSearchQ(""); setEmpResults([]);
    setIncludeOpen(true);
  };
  const saveInclude = async () => {
    if (!includeForm.work_subsector_id) {
      toast.error("Selecione o sub-setor");
      return;
    }
    try {
      if (includeForm.mode === "position") {
        // Cria posição sem colaborador (Fase 2 refactor)
        if (!(includeForm.cargo || "").trim()) {
          toast.error("Informe o cargo da posição.");
          return;
        }
        if (includeForm.status === "aguardando_demanda" && !includeForm.data_prevista_necessidade) {
          toast.error("Informe a data prevista de necessidade para 'Aguardando demanda'.");
          return;
        }
        await api.post(`/work-boards/${id}/positions`, {
          work_board_id: id,
          work_subsector_id: includeForm.work_subsector_id,
          work_shift_type: includeForm.work_shift_type,
          cargo: includeForm.cargo.trim(),
          status: includeForm.status,
          data_prevista_necessidade: includeForm.data_prevista_necessidade || null,
          observacoes: includeForm.work_notes || null,
        });
        toast.success("Posição criada no quadro");
      } else {
        if (!includeForm.employee_id) {
          toast.error("Selecione um colaborador");
          return;
        }
        await api.post(`/work-boards/${id}/include-employee`, {
          employee_id: includeForm.employee_id,
          work_subsector_id: includeForm.work_subsector_id,
          work_shift_type: includeForm.work_shift_type,
          work_notes: includeForm.work_notes,
        });
        toast.success("Colaborador incluído no quadro");
        triggerFlash(includeForm.employee_id);
      }
      setIncludeOpen(false);
      loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Edição de posição vazia existente (Fase 2)
  const [vacancyEdit, setVacancyEdit] = useState(null);
  const openVacancyEdit = (v) => {
    setVacancyEdit({
      id: v.id, cargo: v.cargo || "", status: v.status || "vaga_aberta",
      data_prevista_necessidade: v.data_prevista_necessidade || "",
      observacoes: v.observacoes || "", employee_id: "",
      emp_search: "", emp_results: [],
    });
  };
  const saveVacancyEdit = async () => {
    if (!vacancyEdit) return;
    try {
      const body = {
        cargo: vacancyEdit.cargo,
        status: vacancyEdit.status,
        data_prevista_necessidade: vacancyEdit.data_prevista_necessidade || null,
        observacoes: vacancyEdit.observacoes || null,
      };
      if (vacancyEdit.employee_id) body.employee_id = vacancyEdit.employee_id;
      await api.put(`/work-positions/${vacancyEdit.id}`, body);
      toast.success(vacancyEdit.employee_id ? "Colaborador vinculado à posição" : "Posição atualizada");
      setVacancyEdit(null);
      loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteVacancy = async (v) => {
    if (!window.confirm(`Remover posição "${v.cargo}"?`)) return;
    try {
      await api.delete(`/work-positions/${v.id}`);
      toast.success("Posição removida");
      loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ------- Move employee -------
  const openMove = (emp, currentSub, currentShift) => {
    setMoveCtx({ employee: emp, currentSub, currentShift });
    setMoveOpen(true);
  };
  const doMove = async (targetSub, targetShift) => {
    try {
      await api.post(`/work-boards/${id}/move-employee`, {
        employee_id: moveCtx.employee.id,
        work_subsector_id: targetSub,
        work_shift_type: targetShift,
      });
      toast.success("Colaborador movido");
      setMoveOpen(false);
      triggerFlash(moveCtx.employee.id);
      loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ------- Remove employee -------
  const openRemove = (emp) => { setRemoveCtx(emp); setClearFields(false); setRemoveOpen(true); };
  const doRemove = async () => {
    try {
      await api.post(`/work-boards/${id}/remove-employee`, {
        employee_id: removeCtx.id,
        clear_employee_fields: clearFields,
      });
      toast.success("Colaborador removido do quadro");
      setRemoveOpen(false);
      loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ------- Fix inconsistencies (bulk unlink) -------
  const fixAll = async () => {
    if (!grid?.inconsistencies?.length) return;
    if (!window.confirm(`Desvincular do quadro os ${grid.inconsistencies.length} colaboradores com inconsistências?`)) return;
    try {
      const actions = grid.inconsistencies.map(i => ({ employee_id: i.employee_id, action: "unlink" }));
      const { data } = await api.post(`/work-boards/${id}/fix-inconsistencies`, { actions });
      toast.success(`${data.fixed} inconsistência(s) corrigida(s)`);
      setInconsOpen(false);
      loadGrid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ------- History -------
  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/work-boards/${id}/history?page=1&page_size=100`);
      setHistory(data.items || []);
      setHistoryOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ------- Export Excel -------
  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/work-boards/${id}/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `quadro_${grid?.board?.name || id}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Falha ao exportar"); }
  };

  // ------- Export PDF -------
  const exportPdf = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/work-boards/${id}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `quadro-${(grid?.board?.sector_name || id).replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Relatório PDF gerado");
    } catch { toast.error("Falha ao gerar PDF"); }
  };

  // ------- Export Word (.docx) -------
  const exportDocx = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/work-boards/${id}/report.docx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `quadro-${(grid?.board?.sector_name || id).replace(/\s+/g, "-")}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Relatório Word gerado");
    } catch { toast.error("Falha ao gerar Word"); }
  };

  if (loading || !grid) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const { board, shifts, subsectors, indicators } = grid;

  return (
    <div className="space-y-5" data-testid="work-board-detail">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-6 text-white">
        <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
        <div className="relative">
          <Link to="/quadros-trabalho" className="inline-flex items-center gap-1.5 text-[11.5px] text-white/60 hover:text-white" data-testid="wb-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Todos os quadros
          </Link>
          <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium">
                {board.sector_name} · {board.shift_mode === "FOUR_SHIFTS" ? "4 turnos" : "3 turnos"}
              </div>
              <h1 className="mt-1 font-display text-3xl md:text-[32px] font-semibold tracking-tight">
                {board.name}
              </h1>
              {board.description && (
                <p className="mt-2 text-[13px] text-white/70 max-w-[720px]">{board.description}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={loadGrid} className="border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="wb-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Atualizar
              </Button>
              {canEdit && (
                <Button size="sm" onClick={() => openInclude()} className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="wb-include-btn">
                  <UserPlus className="h-3.5 w-3.5 mr-1" /> Incluir colaborador
                </Button>
              )}
              {canEdit && (
                <Button size="sm" variant="outline" onClick={openNewSub} className="border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="wb-newsub-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Sub-setor
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={exportExcel} className="border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="wb-export-btn">
                <FileDown className="h-3.5 w-3.5 mr-1" /> Excel
              </Button>
              <Button size="sm" onClick={exportPdf}
                      className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F] font-medium"
                      data-testid="wb-export-pdf-btn">
                <FileDown className="h-3.5 w-3.5 mr-1" /> Gerar em PDF
              </Button>
              <Button size="sm" onClick={exportDocx}
                      className="bg-[#1E40AF] hover:bg-[#1E4FBF] text-white font-medium"
                      data-testid="wb-export-docx-btn">
                <FileDown className="h-3.5 w-3.5 mr-1" /> Gerar em Word
              </Button>
              <Button size="sm" variant="outline" onClick={loadHistory} className="border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="wb-history-btn">
                <HistoryIcon className="h-3.5 w-3.5 mr-1" /> Histórico
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="wb-kpis">
        <KpiChip label="Total" value={indicators.total_employees} accent />
        <KpiChip label="Administ." value={indicators.total_admin} />
        <KpiChip label="Turno A" value={indicators.total_shift_a} />
        <KpiChip label="Turno B" value={indicators.total_shift_b} />
        <KpiChip label="Turno C" value={indicators.total_shift_c} />
        {shifts.includes("D") && <KpiChip label="Turno D" value={indicators.total_shift_d} />}
        <button
          onClick={() => setInconsOpen(true)}
          data-testid="wb-inconsistencies-btn"
          className={`rounded-lg border p-3 text-left transition-colors ${
            indicators.total_inconsistencies > 0
              ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
              : "border-border bg-white hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <AlertTriangle className="h-3 w-3" /> Inconsist.
          </div>
          <div className={`font-display text-xl font-semibold mt-0.5 ${indicators.total_inconsistencies > 0 ? "text-amber-700" : "text-slate-700"}`}>
            {indicators.total_inconsistencies}
          </div>
        </button>
      </div>

      {/* Filter bar */}
      <div className="rounded-lg border border-border bg-white p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar nome, matrícula ou cargo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="wb-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setCompact(c => !c)} data-testid="wb-toggle-view">
          <Eye className="h-3.5 w-3.5 mr-1" /> {compact ? "Visão detalhada" : "Visão compacta"}
        </Button>
      </div>

      {/* Grid */}
      {subsectors.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-14 text-center">
          <LayoutGrid className="h-9 w-9 text-[#C5A059] mx-auto mb-3" />
          <div className="font-display text-xl font-semibold">Este quadro ainda não tem sub-setores</div>
          <p className="mt-2 text-[13px] text-muted-foreground max-w-[420px] mx-auto">
            Crie o primeiro sub-setor para começar a distribuir colaboradores.
          </p>
          {canEdit && (
            <Button onClick={openNewSub} className="mt-6 bg-[#234935] hover:bg-[#2c5c43] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Criar sub-setor
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]" data-testid="wb-grid-table">
              <thead>
                <tr className="bg-slate-50 border-b border-border">
                  <th className="sticky left-0 bg-slate-50 z-10 px-4 py-3 text-left font-semibold text-slate-700 w-[200px] min-w-[200px]">
                    Sub-setor
                  </th>
                  {shifts.map(sh => (
                    <th key={sh} className="px-3 py-3 text-left font-semibold text-slate-700 min-w-[220px] border-l border-border/60">
                      <div className="flex items-center gap-2">
                        <span>{SHIFT_LABELS[sh]}</span>
                        <Badge variant="outline" className="text-[10px]">{indicators.totals_by_shift[sh] || 0}</Badge>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subsectors.map((sub, idx) => (
                  <tr key={sub.id} className={idx % 2 ? "bg-slate-50/40" : ""}>
                    <td className="sticky left-0 bg-inherit z-10 px-4 py-3 border-b border-border/60 font-medium text-slate-800 align-top">
                      <div className="flex items-start justify-between gap-1">
                        <div>
                          <div>{sub.name}</div>
                          <div className="text-[10.5px] text-muted-foreground mt-0.5 uppercase tracking-wider">
                            #{sub.display_order}
                          </div>
                        </div>
                        {canEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground"><MoreVertical className="h-3 w-3" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => openEditSub(sub)}>
                                <Pencil className="h-3 w-3 mr-2" /> Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => removeSub(sub)} className="text-red-600">
                                <Trash2 className="h-3 w-3 mr-2" /> Remover
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                    {shifts.map(sh => {
                      const emps = filterCells[sub.id]?.[sh] || [];
                      return (
                        <td key={sh} className="px-2 py-2 align-top border-l border-b border-border/60 min-h-[90px]" data-testid={`wb-cell-${sub.id}-${sh}`}>
                          {emps.length === 0 ? (
                            canEdit ? (
                              <button
                                onClick={() => openInclude(sub.id, sh)}
                                className="w-full h-full min-h-[70px] rounded-md border border-dashed border-slate-200 hover:border-[#C5A059]/50 hover:bg-[#C5A059]/5 text-[11px] text-muted-foreground hover:text-[#8A6B2B] transition-colors flex items-center justify-center gap-1 group"
                              >
                                <Plus className="h-3 w-3 opacity-50 group-hover:opacity-100" /> Incluir
                              </button>
                            ) : (
                              <div className="text-[11px] text-muted-foreground italic px-2 py-3">—</div>
                            )
                          ) : (
                            <div className="space-y-1.5">
                              {emps.map(e => (
                                e.is_vacancy ? (
                                  <VacancyCard
                                    key={e.id}
                                    vacancy={e}
                                    canEdit={canEdit}
                                    onEdit={() => openVacancyEdit(e)}
                                    onDelete={() => deleteVacancy(e)}
                                  />
                                ) : (
                                  <EmployeeCard
                                    key={e.id}
                                    emp={e}
                                    compact={compact}
                                    flash={flash === e.id}
                                    canEdit={canEdit}
                                    onMove={() => openMove(e, sub.id, sh)}
                                    onRemove={() => openRemove(e)}
                                    onView={() => nav(`/colaboradores?search=${encodeURIComponent(e.matricula || e.nome)}`)}
                                  />
                                )
                              ))}
                              {canEdit && (
                                <button
                                  onClick={() => openInclude(sub.id, sh)}
                                  data-testid={`wb-add-more-${sub.id}-${sh}`}
                                  className="w-full py-1 rounded-md border border-dashed border-slate-200 hover:border-[#C5A059]/50 hover:bg-[#C5A059]/5 text-[10px] text-muted-foreground hover:text-[#8A6B2B] transition-colors"
                                >
                                  + Incluir
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- Subsector dialog --- */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{subEditing ? "Editar sub-setor" : "Novo sub-setor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Nome</Label>
              <Input
                value={subForm.name}
                onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                data-testid="wb-sub-name"
                placeholder="Ex.: Recebimento, Expedição..."
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Ordem de exibição</Label>
              <Input type="number" min="1" value={subForm.display_order}
                     onChange={(e) => setSubForm({ ...subForm, display_order: Number(e.target.value) })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubOpen(false)}>Cancelar</Button>
            <Button onClick={saveSub} className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="wb-sub-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Include dialog (colaborador OU posição vaga) --- */}
      <Dialog open={includeOpen} onOpenChange={setIncludeOpen}>
        <DialogContent className="max-w-[560px]" data-testid="wb-include-dialog">
          <DialogHeader>
            <DialogTitle>Incluir no quadro</DialogTitle>
            <DialogDescription>
              Adicione um colaborador ou crie uma posição de vaga para preencher depois.
            </DialogDescription>
          </DialogHeader>

          {/* Toggle: com colaborador ou posição vaga */}
          <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 p-1 bg-slate-50/50">
            <button
              type="button"
              onClick={() => setIncludeForm(f => ({ ...f, mode: "employee" }))}
              data-testid="wb-include-mode-employee"
              className={`px-3 py-2 rounded-md text-[12.5px] font-medium transition ${
                includeForm.mode === "employee" ? "bg-white shadow-sm text-[#234935]" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Com colaborador
            </button>
            <button
              type="button"
              onClick={() => setIncludeForm(f => ({ ...f, mode: "position", employee_id: "" }))}
              data-testid="wb-include-mode-position"
              className={`px-3 py-2 rounded-md text-[12.5px] font-medium transition ${
                includeForm.mode === "position" ? "bg-white shadow-sm text-[#234935]" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Posição sem colaborador
            </button>
          </div>

          <div className="space-y-4 py-2">
            {includeForm.mode === "employee" ? (
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Buscar colaborador</Label>
                <Input
                  placeholder="Digite nome ou matrícula..."
                  value={empSearchQ}
                  onChange={(e) => setEmpSearchQ(e.target.value)}
                  data-testid="wb-include-search"
                />
                {empResults.length > 0 && (
                  <div className="mt-2 max-h-[200px] overflow-y-auto rounded-lg border border-border divide-y divide-border">
                    {empResults.map(emp => (
                      <button
                        key={emp.id}
                        onClick={() => {
                          setIncludeForm(f => ({ ...f, employee_id: emp.id }));
                          setEmpSearchQ(`${emp.matricula} - ${emp.nome}`);
                          setEmpResults([]);
                        }}
                        data-testid={`wb-include-emp-${emp.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                      >
                        <div className="text-[13px] font-medium">{emp.nome}</div>
                        <div className="text-[11px] text-muted-foreground">
                          Matrícula: {emp.matricula || "—"} · {emp.cargo || "—"}
                          {emp.work_board_id && emp.work_board_id !== id && <span className="text-amber-600"> · (já em outro quadro)</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Cargo (Função)</Label>
                  <Input
                    value={includeForm.cargo || ""}
                    onChange={(e) => setIncludeForm({ ...includeForm, cargo: e.target.value })}
                    placeholder="Ex.: Mecânico II"
                    data-testid="wb-include-cargo"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Status da posição</Label>
                  <Select
                    value={includeForm.status}
                    onValueChange={(v) => setIncludeForm({ ...includeForm, status: v })}
                  >
                    <SelectTrigger data-testid="wb-include-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vaga_aberta">Vaga em aberto</SelectItem>
                      <SelectItem value="aguardando_demanda">Aguardando demanda</SelectItem>
                      <SelectItem value="em_entrevista">Em entrevista</SelectItem>
                      <SelectItem value="em_curso_obrigatorio">Em curso obrigatório</SelectItem>
                      <SelectItem value="em_exame_admissional">Em exame admissional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {includeForm.status === "aguardando_demanda" && (
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider">Data prevista de necessidade *</Label>
                    <DateInputBR
                      value={includeForm.data_prevista_necessidade || ""}
                      onChange={(v) => setIncludeForm({ ...includeForm, data_prevista_necessidade: v })}
                      testId="wb-include-data-prev"
                    />
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Sub-setor</Label>
                <Select value={includeForm.work_subsector_id} onValueChange={(v) => setIncludeForm({ ...includeForm, work_subsector_id: v })}>
                  <SelectTrigger data-testid="wb-include-sub"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {subsectors.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Turno</Label>
                <Select value={includeForm.work_shift_type} onValueChange={(v) => setIncludeForm({ ...includeForm, work_shift_type: v })}>
                  <SelectTrigger data-testid="wb-include-shift"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {shifts.map(sh => <SelectItem key={sh} value={sh}>{SHIFT_LABELS[sh]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Observação (opcional)</Label>
              <Textarea rows={2} value={includeForm.work_notes || ""} onChange={(e) => setIncludeForm({ ...includeForm, work_notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncludeOpen(false)}>Cancelar</Button>
            <Button onClick={saveInclude} className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="wb-include-save">
              {includeForm.mode === "position" ? "Criar posição" : "Incluir no quadro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Move dialog --- */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Mover colaborador</DialogTitle>
            <DialogDescription>
              {moveCtx?.employee?.nome} — selecione o novo sub-setor e turno.
            </DialogDescription>
          </DialogHeader>
          {moveCtx && (
            <MoveForm
              currentSub={moveCtx.currentSub}
              currentShift={moveCtx.currentShift}
              subsectors={subsectors}
              shifts={shifts}
              onCancel={() => setMoveOpen(false)}
              onConfirm={doMove}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* --- Remove dialog --- */}
      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Remover colaborador do quadro</DialogTitle>
            <DialogDescription>
              {removeCtx?.nome} — escolha como proceder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:border-[#C5A059]/50">
              <input type="radio" checked={!clearFields} onChange={() => setClearFields(false)} className="mt-1" />
              <div>
                <div className="text-[13px] font-medium">Remover apenas deste quadro</div>
                <div className="text-[11.5px] text-muted-foreground">Mantém os dados operacionais (sub-setor e turno) no cadastro do colaborador.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:border-red-300">
              <input type="radio" checked={clearFields} onChange={() => setClearFields(true)} className="mt-1" />
              <div>
                <div className="text-[13px] font-medium text-red-700">Remover e limpar cadastro</div>
                <div className="text-[11.5px] text-muted-foreground">Limpa quadro, sub-setor e turno do cadastro do colaborador.</div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveOpen(false)}>Cancelar</Button>
            <Button onClick={doRemove} className={clearFields ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#234935] hover:bg-[#2c5c43] text-white"} data-testid="wb-remove-confirm">
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Inconsistencies dialog --- */}
      <Dialog open={inconsOpen} onOpenChange={setInconsOpen}>
        <DialogContent className="max-w-[680px]">
          <DialogHeader>
            <DialogTitle>Inconsistências detectadas</DialogTitle>
            <DialogDescription>
              Colaboradores com dados operacionais incompletos ou inválidos neste quadro.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[440px] overflow-y-auto">
            {grid.inconsistencies?.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-[13px]">
                Tudo certo! Nenhuma inconsistência encontrada. 🎉
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Severidade</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grid.inconsistencies.map(i => (
                    <TableRow key={i.id}>
                      <TableCell className="text-[12.5px]">{i.employee_name}</TableCell>
                      <TableCell className="text-[12px] text-muted-foreground">{i.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          i.severity === "HIGH" ? "border-red-300 text-red-700 bg-red-50"
                          : i.severity === "MEDIUM" ? "border-amber-300 text-amber-700 bg-amber-50"
                          : "border-slate-300 text-slate-700 bg-slate-50"
                        }>{i.severity}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInconsOpen(false)}>Fechar</Button>
            {grid.inconsistencies?.length > 0 && canAdmin && (
              <Button onClick={fixAll} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="wb-fix-all">
                Desvincular todos
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Vacancy edit dialog (posição sem colaborador — Fase 2 refactor) --- */}
      <Dialog open={!!vacancyEdit} onOpenChange={(v) => !v && setVacancyEdit(null)}>
        <DialogContent className="max-w-[560px]" data-testid="wb-vacancy-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar posição</DialogTitle>
            <DialogDescription>
              Ajuste o status, vincule um colaborador ou atualize os dados desta posição.
            </DialogDescription>
          </DialogHeader>
          {vacancyEdit && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Cargo (Função)</Label>
                <Input
                  value={vacancyEdit.cargo}
                  onChange={(e) => setVacancyEdit({ ...vacancyEdit, cargo: e.target.value })}
                  data-testid="wb-vac-cargo"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Status</Label>
                <Select
                  value={vacancyEdit.status}
                  onValueChange={(v) => setVacancyEdit({ ...vacancyEdit, status: v })}
                >
                  <SelectTrigger data-testid="wb-vac-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vaga_aberta">Vaga em aberto</SelectItem>
                    <SelectItem value="aguardando_demanda">Aguardando demanda</SelectItem>
                    <SelectItem value="em_entrevista">Em entrevista</SelectItem>
                    <SelectItem value="em_curso_obrigatorio">Em curso obrigatório</SelectItem>
                    <SelectItem value="em_exame_admissional">Em exame admissional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {vacancyEdit.status === "aguardando_demanda" && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Data prevista *</Label>
                  <DateInputBR
                    value={vacancyEdit.data_prevista_necessidade || ""}
                    onChange={(v) => setVacancyEdit({ ...vacancyEdit, data_prevista_necessidade: v })}
                    testId="wb-vac-data-prev"
                  />
                </div>
              )}
              <div className="border-t border-slate-200 pt-3">
                <Label className="text-[11px] uppercase tracking-wider">Vincular colaborador (opcional)</Label>
                <Input
                  placeholder="Digite nome ou matrícula para buscar…"
                  value={vacancyEdit.emp_search}
                  onChange={async (e) => {
                    const q = e.target.value;
                    setVacancyEdit((s) => ({ ...s, emp_search: q, employee_id: "" }));
                    if (q.length >= 2) {
                      try {
                        const { data } = await api.get(`/employees?search=${encodeURIComponent(q)}&status=ativo&page_size=8`);
                        setVacancyEdit((s) => s ? ({ ...s, emp_results: data.items || [] }) : s);
                      } catch { /* noop */ }
                    } else {
                      setVacancyEdit((s) => s ? ({ ...s, emp_results: [] }) : s);
                    }
                  }}
                  data-testid="wb-vac-emp-search"
                />
                {vacancyEdit.emp_results?.length > 0 && (
                  <div className="mt-2 max-h-[180px] overflow-y-auto rounded-lg border border-border divide-y divide-border">
                    {vacancyEdit.emp_results.map(emp => (
                      <button
                        key={emp.id}
                        onClick={() => setVacancyEdit({
                          ...vacancyEdit,
                          employee_id: emp.id,
                          emp_search: `${emp.matricula} - ${emp.nome}`,
                          emp_results: [],
                        })}
                        data-testid={`wb-vac-emp-${emp.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                      >
                        <div className="text-[13px] font-medium">{emp.nome}</div>
                        <div className="text-[11px] text-muted-foreground">
                          Matrícula: {emp.matricula || "—"} · {emp.cargo || "—"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {vacancyEdit.employee_id && (
                  <div className="mt-2 text-[11.5px] text-emerald-700">
                    Colaborador será vinculado → status ficará <b>Preenchida</b>.
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Observações</Label>
                <Textarea
                  rows={2}
                  value={vacancyEdit.observacoes || ""}
                  onChange={(e) => setVacancyEdit({ ...vacancyEdit, observacoes: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVacancyEdit(null)}>Cancelar</Button>
            <Button onClick={saveVacancyEdit} className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="wb-vac-save">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- History dialog --- */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-[820px]">
          <DialogHeader>
            <DialogTitle>Histórico de movimentações</DialogTitle>
            <DialogDescription>
              Últimas 100 ações realizadas neste quadro (com origem e usuário).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[520px] overflow-y-auto">
            {history.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-[13px]">Sem movimentações registradas.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Por</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h.id}>
                      <TableCell className="text-[11.5px] font-mono tabular-nums">{new Date(h.created_at).toLocaleString("pt-BR")}</TableCell>
                      <TableCell className="text-[12.5px]">
                        <div className="font-medium">{h.employee_name || "—"}</div>
                        <div className="text-[10.5px] text-muted-foreground">{h.employee_matricula}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-[10.5px]">{h.action_type}</Badge></TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">{h.change_source}</TableCell>
                      <TableCell className="text-[11.5px]">{h.changed_by_user_name || "Sistema"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== Components =====================

function KpiChip({ label, value, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-[#234935]/30 bg-[#234935]/5" : "border-border bg-white"}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-xl font-semibold mt-0.5 tabular-nums ${accent ? "text-[#234935]" : "text-slate-800"}`}>{value ?? 0}</div>
    </div>
  );
}

const VACANCY_STATUS_STYLE = {
  vaga_aberta:            { label: "Vaga em aberto",        cls: "bg-amber-50 border-amber-200 text-amber-800" },
  aguardando_demanda:     { label: "Aguardando demanda",    cls: "bg-slate-50 border-slate-200 text-slate-700" },
  em_entrevista:          { label: "Em entrevista",         cls: "bg-blue-50 border-blue-200 text-blue-800" },
  em_curso_obrigatorio:   { label: "Em curso obrigatório",  cls: "bg-indigo-50 border-indigo-200 text-indigo-800" },
  em_exame_admissional:   { label: "Em exame admissional",  cls: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800" },
};

function VacancyCard({ vacancy, canEdit, onEdit, onDelete }) {
  const s = VACANCY_STATUS_STYLE[vacancy.status] || VACANCY_STATUS_STYLE.vaga_aberta;
  return (
    <div
      data-testid={`wb-vacancy-${vacancy.id}`}
      className={`group rounded-lg border-2 border-dashed p-2 text-[11.5px] ${s.cls}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{vacancy.cargo || "—"}</div>
          <div className="text-[10.5px] mt-0.5 opacity-80">
            {s.label}
            {vacancy.status === "aguardando_demanda" && vacancy.data_prevista_necessidade && (
              <> · previsão {fmtDate(vacancy.data_prevista_necessidade)}</>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
            <button
              onClick={onEdit}
              title="Editar / vincular colaborador"
              className="p-1 rounded hover:bg-white/60"
              data-testid={`wb-vac-edit-${vacancy.id}`}
            >
              <Edit2 className="h-3 w-3" />
            </button>
            <button
              onClick={onDelete}
              title="Remover posição"
              className="p-1 rounded hover:bg-white/60 text-rose-600"
              data-testid={`wb-vac-del-${vacancy.id}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EmployeeCard({ emp, compact, flash, canEdit, onMove, onRemove, onView }) {
  return (
    <div
      data-testid={`wb-emp-${emp.id}`}
      className={`rounded-md border px-2.5 py-1.5 text-[12px] bg-white transition-all ${
        flash ? "ring-2 ring-[#C5A059] bg-[#C5A059]/5" : "border-slate-200 hover:border-[#C5A059]/50"
      }`}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-slate-800">{emp.nome}</div>
          <div className="text-[10.5px] text-muted-foreground font-mono tabular-nums">
            {emp.matricula || "—"}
            {!compact && emp.cargo && <span className="font-sans"> · {emp.cargo}</span>}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-muted-foreground" data-testid={`wb-emp-actions-${emp.id}`}>
              <MoreVertical className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onView}>
              <Eye className="h-3 w-3 mr-2" /> Ver cadastro
            </DropdownMenuItem>
            {canEdit && (
              <>
                <DropdownMenuItem onSelect={onMove}>
                  <Settings2 className="h-3 w-3 mr-2" /> Mover / editar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onRemove} className="text-red-600">
                  <Trash2 className="h-3 w-3 mr-2" /> Remover do quadro
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function MoveForm({ currentSub, currentShift, subsectors, shifts, onCancel, onConfirm }) {
  const [sub, setSub] = useState(currentSub);
  const [shift, setShift] = useState(currentShift);
  return (
    <>
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Novo sub-setor</Label>
            <Select value={sub} onValueChange={setSub}>
              <SelectTrigger data-testid="wb-move-sub"><SelectValue /></SelectTrigger>
              <SelectContent>
                {subsectors.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Novo turno</Label>
            <Select value={shift} onValueChange={setShift}>
              <SelectTrigger data-testid="wb-move-shift"><SelectValue /></SelectTrigger>
              <SelectContent>
                {shifts.map(sh => <SelectItem key={sh} value={sh}>{SHIFT_LABELS[sh]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button
          disabled={sub === currentSub && shift === currentShift}
          onClick={() => onConfirm(sub, shift)}
          className="bg-[#234935] hover:bg-[#2c5c43] text-white"
          data-testid="wb-move-confirm"
        >
          Mover colaborador
        </Button>
      </DialogFooter>
    </>
  );
}
